"use client";

import type { CareSeekerShallowLeadFormDataV2 } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroVariant } from "../shared/hero-variants";

import Loading from "@homeinstead/icons/Loading";
import Service from "@homeinstead/icons/Service";
import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { Checkbox } from "@homeinstead/ui/components/Checkbox";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { Link } from "@homeinstead/ui/components/Link";
import { PhoneInput } from "@homeinstead/ui/components/PhoneInput";
import { Controller, useFormContext } from "react-hook-form";
import { useLocale } from "~www/components/LocaleProvider";
import { getHeroVariantStyles } from "../shared/hero-variants";
import { Testimony } from "../Testimony";

const buttonVariants: Record<HeroVariant, "primary" | "secondary"> = {
  default: "primary",
  light: "secondary",
};

export const ContactStepV5 = ({
  isPending,
  className,
  variant = "default",
}: {
  isPending: boolean;
  className?: string;
  variant?: HeroVariant;
}) => {
  const locale = useLocale();
  const siteUrl = locale.metadata.canonicalOrigin;

  const { register, control, formState } =
    useFormContext<CareSeekerShallowLeadFormDataV2>();
  const { errors, touchedFields, submitCount } = formState;

  const showError = (field: "email" | "phone" | "consentTerms") =>
    (touchedFields?.[field] || submitCount > 0) && errors?.[field];

  const { textColorClass, errorColorClass } = getHeroVariantStyles(variant);

  return (
    <div
      className={cn("flex h-full w-full flex-col gap-4 xl:flex-row", className)}
    >
      <div className="flex flex-col gap-4 md:w-[440px]">
        <h1
          className={cn(
            "mb-2 text-left font-bold font-jakarta md:mb-0",
            "text-xl md:text-1xl",
            textColorClass,
          )}
        >
          How should we reach you?
        </h1>
        <p className={cn("text-base xl:mb-2", textColorClass)}>
          <span className="font-bold">
            Your contact details are only used for a phone consultation.
          </span>{" "}
          We will never share or sell your personal information.
        </p>

        <div className="mb-4 flex flex-col gap-6 md:mb-2">
          <div>
            <Input
              placeholder="Email Address"
              type="email"
              {...register("email")}
              className="bg-white text-base text-new-beginnings-base"
            />
            {showError("email") && (
              <FormError.span className={errorColorClass}>
                {errors?.email?.message}
              </FormError.span>
            )}
          </div>

          <PhoneInput<CareSeekerShallowLeadFormDataV2>
            className="bg-white text-base text-new-beginnings-base"
            errorClassName={errorColorClass}
            fieldName="phone"
            showErrorAfterSubmit
          />
        </div>

        <Controller
          control={control}
          name="consentTerms"
          render={({ field }) => (
            <div className="flex items-start gap-2">
              <Checkbox
                checked={field.value}
                id="consentTerms"
                name={field.name}
                onChange={field.onChange}
              />
              <label
                className={cn("text-xs leading-5", textColorClass)}
                htmlFor="consentTerms"
              >
                <div>
                  By checking this box, I agree to receive SMS text messages
                  from Home Instead at the phone number provided. Message & data
                  rates may apply. Message frequency may vary based on service
                  needs and SMS activity. Reply STOP to opt out of further
                  messaging. For more details, including our SMS terms, please
                  refer to our{" "}
                  <Link
                    className="text-xs underline hover:no-underline"
                    href={`${siteUrl}/legal/privacy-policy`}
                    rel="noopener noreferrer"
                    size="inline"
                    target="_blank"
                    variant="plain"
                  >
                    Privacy Policy
                  </Link>
                  .
                </div>
              </label>
            </div>
          )}
        />
        {showError("consentTerms") && (
          <FormError.span className={errorColorClass}>
            {errors?.consentTerms?.message}
          </FormError.span>
        )}

        <Button
          className="mt-4 h-12 w-full rounded-full"
          disabled={isPending}
          loading={isPending}
          type="submit"
          variant={buttonVariants[variant]}
        >
          {isPending ? (
            <Loading className="h-8 w-8 fill-new-beginnings-base" />
          ) : (
            "Contact me with care options"
          )}
        </Button>

        <span className={cn("text-xs leading-5", textColorClass)}>
          By clicking &quot;Contact me with care options&quot; you agree to our{" "}
          <Link
            className="text-xs underline hover:no-underline"
            href={`${siteUrl}/legal/client-notice-of-privacy-practices`}
            rel="noopener noreferrer"
            size="inline"
            target="_blank"
            variant="plain"
          >
            Privacy Policy
          </Link>
          .
        </span>
      </div>
      <Testimony
        className="mb-auto ml-auto hidden max-w-md flex-col items-end justify-end gap-4 xl:flex"
        customIcon={<Service />}
        variant={variant}
      >
        <div className="flex flex-col gap-2 text-end md:max-w-[400px]">
          <div className="font-bold">We&apos;re here to help</div>
          <div>
            We&apos;re standing by and ready to provide support often with home
            care starting in 48 hours.
          </div>
        </div>
      </Testimony>
    </div>
  );
};
